import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { batchAPI, rankingAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import '../styles/BatchLeaderboard.css';

const BatchLeaderboard = () => {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();

  const [batch, setBatch] = useState(null);
  const [rankings, setRankings] = useState([]);
  const [userRank, setUserRank] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRankings, setTotalRankings] = useState(0);
  const limit = 50;

  useEffect(() => {
    fetchBatchDetails();
    if (isAuthenticated) {
      checkAccessAndFetchRankings();
    } else {
      setCheckingAccess(false);
      setLoading(false);
    }
  }, [batchId, isAuthenticated, currentPage]);

  const fetchBatchDetails = async () => {
    try {
      const response = await batchAPI.getBatchDetails(batchId);
      if (response.data.success) {
        setBatch(response.data.batch);
      }
    } catch (error) {
      console.error('Error fetching batch details:', error);
    }
  };

  const checkAccessAndFetchRankings = async () => {
    try {
      setCheckingAccess(true);
      setLoading(true);
      
      // Check access
      const accessResponse = await batchAPI.checkBatchAccess(batchId);
      const access = accessResponse.data.hasAccess || accessResponse.data.isAdmin;
      setHasAccess(access);

      if (access) {
        // Fetch rankings
        await fetchRankings();
      }
    } catch (error) {
      console.error('Error checking access:', error);
      setHasAccess(false);
    } finally {
      setCheckingAccess(false);
      setLoading(false);
    }
  };

  const fetchRankings = async () => {
    try {
      const response = await rankingAPI.getBatchRankings(batchId, currentPage, limit);
      
      if (response.data.success) {
        // console.log("Response of get batch rankings: ", response)
        setRankings(response.data.rankings || []);
        // console.log("get batch rankings: ", rankings)
        setTotalPages(response.data.pagination.totalPages || 1);
        // console.log("get batch rankings: ", rankings)
        setTotalRankings(response.data.totalParticipants || 0);
        
        // Find current user's rank in the list
        if (user) {
          const currentUserRanking = response.data.rankings.find(
            ranking => ranking.userId._id === user.id || ranking.userId === user.id
          );
          if (currentUserRanking) {
            setUserRank(currentUserRanking);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching rankings:', error);
    }
  };

  const getMedalIcon = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 80) return '#10b981';
    if (percentage >= 60) return '#f59e0b';
    return '#ef4444';
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Loading state
  if (checkingAccess || loading) {
    return (
      <div>
        <Navbar />
        <div className="loading-container">
          <div className="loader"></div>
          <p>Loading leaderboard...</p>
        </div>
      </div>
    );
  }

  // Not authenticated
  if (!isAuthenticated) {
    return (
      <div>
        <Navbar />
        <div className="access-denied-container">
          <div className="access-denied-card">
            <div className="access-denied-icon">🔒</div>
            <h2>Login Required</h2>
            <p>Please login to view the leaderboard</p>
            <button onClick={() => navigate('/login')} className="btn-primary">
              Login with Google
            </button>
          </div>
        </div>
      </div>
    );
  }

  // No access
  if (!hasAccess && user?.role !== 'admin') {
    return (
      <div>
        <Navbar />
        <div className="access-denied-container">
          <div className="access-denied-card">
            <div className="access-denied-icon">⛔</div>
            <h2>Enrollment Required</h2>
            <p>You need to enroll in <strong>{batch?.name}</strong> to view the leaderboard</p>
            {batch && (
              <div className="batch-info-preview">
                <h4>{batch.name}</h4>
                <p>📚 {batch.subject}</p>
                {batch.price > 0 && <p className="price">₹{batch.price}</p>}
              </div>
            )}
            <button 
              onClick={() => navigate(`/premium-batch/${batchId}`)} 
              className="btn-success"
            >
              View Batch Details
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="leaderboard-page">
      <Navbar />

      {/* Header */}
      <div className="leaderboard-header">
        <div className="container">
          <button 
            onClick={() => navigate(`/premium-batch/${batchId}`)}
            className="btn-back"
          >
            ← Back to Batch
          </button>
          
          <div className="header-content">
            <div className="header-icon">🏆</div>
            <h1>Leaderboard</h1>
            <p className="batch-name">{batch?.name}</p>
            <p className="total-participants">
              {totalRankings} {totalRankings === 1 ? 'Participant' : 'Participants'}
            </p>
          </div>
        </div>
      </div>

      {/* User's Rank Card - Show if user is in current page or has rank */}
      {userRank && (
        <div className="container">
          <div className="user-rank-card">
            <div className="rank-badge">
              <span className="rank-number">{getMedalIcon(userRank.rank)}</span>
            </div>
            <div className="rank-details">
              <h3>Your Rank</h3>
              <div className="rank-stats">
                <div className="stat">
                  <span className="stat-label">Total Score</span>
                  <span className="stat-value">{userRank.totalScore || 0}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Quizzes Attempted</span>
                  <span className="stat-value">{userRank.quizzesAttempted || 0}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Avg Score</span>
                  <span className="stat-value">
                    {userRank.averagePercentage ? userRank.averagePercentage.toFixed(1) : 0}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Leaderboard Table */}
      <div className="container">
        <div className="leaderboard-container">
          <div className="leaderboard-table">
            <div className="table-header">
              <div className="header-cell rank-col">Rank</div>
              <div className="header-cell name-col">Student</div>
              <div className="header-cell score-col">Total Score</div>
              <div className="header-cell quizzes-col">Quizzes</div>
              <div className="header-cell avg-col">Avg Score</div>
              <div className="header-cell progress-col">Progress</div>
            </div>

            <div className="table-body">
              {rankings.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">📊</div>
                  <h3>No Rankings Yet</h3>
                  <p>Be the first to attempt a quiz and claim the top spot!</p>
                  <button 
                    onClick={() => navigate(`/premium-tests/${batchId}`)}
                    className="btn-primary"
                  >
                    Start Quiz
                  </button>
                </div>
              ) : (
                rankings.map((ranking, index) => {
                  const studentData = ranking;
                  const isCurrentUser = studentData?.userId === user?.id || studentData === user?.id;
                  const displayRank = ranking.rank || ((currentPage - 1) * limit + index + 1);
                  
                  return (
                    <div 
                      key={studentData?.userId || index} 
                      className={`table-row ${isCurrentUser ? 'current-user' : ''} ${displayRank <= 3 ? 'top-three' : ''}`}
                    >
                      <div className="table-cell rank-col">
                        <span className="rank-badge-small">
                          {getMedalIcon(displayRank)}
                        </span>
                      </div>
                      
                      <div className="table-cell name-col">
                        <div className="student-info">
                          <img 
                            src={
                              studentData?.profilePicture || 
                              `https://ui-avatars.com/api/?name=${encodeURIComponent(studentData?.name || 'User')}&background=random`
                            }
                            alt={studentData?.name || 'User'}
                            className="student-avatar"
                          />
                          <div className="student-details">
                            <span className="student-name">
                              {studentData?.name || 'Anonymous User'}
                              {isCurrentUser && (
                                <span className="you-badge">You</span>
                              )}
                            </span>
                            <span className="student-email">
                              {studentData?.email || ''}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="table-cell score-col">
                        <span className="score-value">{ranking.totalScore || 0}</span>
                      </div>
                      
                      <div className="table-cell quizzes-col">
                        <span className="quiz-count">{ranking.quizzesAttempted || 0}</span>
                      </div>
                      
                      <div className="table-cell avg-col">
                        <span className="avg-score">
                          {ranking.averagePercentage ? ranking.averagePercentage.toFixed(1) : 0}%
                        </span>
                      </div>
                      
                      <div className="table-cell progress-col">
                        <div className="progress-bar">
                          <div 
                            className="progress-fill"
                            style={{
                              width: `${ranking.averagePercentage || 0}%`,
                              backgroundColor: getProgressColor(ranking.averagePercentage || 0)
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination-container">
              <button 
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                ← Previous
              </button>
              
              <div className="pagination-info">
                <span className="page-numbers">
                  Page {currentPage} of {totalPages}
                </span>
                <span className="page-range">
                  Showing {((currentPage - 1) * limit) + 1} - {Math.min(currentPage * limit, totalRankings)} of {totalRankings}
                </span>
              </div>
              
              <button 
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next →
              </button>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BatchLeaderboard;